import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { type GeoLocation } from "@src/appV2/Location";
import { convertToGeoLocation } from "@src/appV2/Location/utils";
import { useWorkerCustomToBaseQualificationNameMap } from "@src/appV2/Qualifications/useWorkerCustomToBaseQualificationNameMap";

import { useShiftBundlesConfig } from "../../Shift/Bundle/useShiftBundlesConfig";
import { useGetOpenShiftsForDates } from "../../Shift/Open/useGetOpenShiftsForDates";
import { type ModalWorkplaceData } from "../../Shift/types";
import { DEFAULT_DISTANCE_IN_MILES_FILTER } from "../../ShiftDiscovery/Filters/constants";
import { filterOpenShifts } from "../../ShiftDiscovery/Filters/filterOpenShifts";
import { useShiftDiscoveryUserFiltersContext } from "../../ShiftDiscovery/Filters/useUserFiltersContext";
import { getShiftDiscoveryDefaultDateRange } from "../../utils/getShiftDiscoveryDefaultDateRange";
import type { WorkplaceProfile } from "../types";

interface UseWorkplaceOpenShiftsDataOptions {
  enabled?: boolean;
}

function getWorkplaceCoordinates(workplace?: WorkplaceProfile | ModalWorkplaceData): GeoLocation {
  if (workplace && "attributes" in workplace) {
    const { latitude, longitude } = workplace.attributes.location;
    return {
      latitude: latitude ?? 0,
      longitude: longitude ?? 0,
    };
  }

  return convertToGeoLocation(workplace?.geoLocation.coordinates ?? [0, 0]);
}

export function useWorkplaceOpenShiftsData(
  workplace?: WorkplaceProfile | ModalWorkplaceData,
  options?: UseWorkplaceOpenShiftsDataOptions
) {
  const { enabled = true } = options ?? {};

  const coordinates = getWorkplaceCoordinates(workplace);
  const workplaceId = workplace && "attributes" in workplace ? workplace.id : workplace?.userId;

  const {
    dates: dateStrings,
    // Filtering by timeslot happens at the list rendering level
    // because each date can have a different set of timeslots
    shiftTimeSlots: timeSlotFilters,
    licenses,
    includeWindowedOutShifts,
  } = useShiftDiscoveryUserFiltersContext();

  const shiftBundlesConfig = useShiftBundlesConfig();

  const windowedOutShiftsEnabled = useCbhFlag(CbhFeatureFlag.INCLUDE_WINDOWED_OUT_SHIFTS, {
    defaultValue: false,
  });

  const includedWindowedOut = windowedOutShiftsEnabled ? (includeWindowedOutShifts ?? true) : false;

  const openShiftsResult = useGetOpenShiftsForDates({
    dates: [],
    dateRange: getShiftDiscoveryDefaultDateRange(180),
    filter: {
      "workplace.id": workplaceId,
      area: {
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        radiusInMiles: DEFAULT_DISTANCE_IN_MILES_FILTER,
      },
      includeWindowedOutShifts: includedWindowedOut,
    },
    enabled:
      enabled && isDefined(workplace) && coordinates.latitude !== 0 && coordinates.longitude !== 0,
  });

  const unfilteredWorkplaceShifts = openShiftsResult.shifts;

  const customToBaseQualificationNameMap = useWorkerCustomToBaseQualificationNameMap();

  const filteredWorkplaceShifts = filterOpenShifts(
    {
      shifts: unfilteredWorkplaceShifts,
      workplacesMap: openShiftsResult.workplacesMap,
    },
    { dates: dateStrings, licenses },
    customToBaseQualificationNameMap
  );

  const workplaceFromOpenShiftsData = isDefined(workplaceId)
    ? openShiftsResult.workplacesMap.get(workplaceId)
    : undefined;
  const isShiftBundlesEnabled =
    (shiftBundlesConfig?.enabled ?? false) &&
    isDefined(workplaceFromOpenShiftsData) &&
    workplaceFromOpenShiftsData.attributes.isShiftBundlesEnabled;
  const orientationShiftKind = workplaceFromOpenShiftsData?.attributes.orientationShiftKind;

  return {
    ...openShiftsResult,
    data: {
      filteredWorkplaceShifts,
      unfilteredWorkplaceShifts,
      workplacesMap: openShiftsResult.workplacesMap,
      rateNegotiationsMap: openShiftsResult.rateNegotiationsMap,
      shiftAuctionsMap: openShiftsResult.shiftAuctionsMap,
      offersMap: openShiftsResult.offersMap,
      isShiftBundlesEnabled,
      orientationShiftKind,
      timeSlotFilters,
    },
  };
}
